import { holdCountdownAt } from './holdCountdown';
import type { BookingMoment } from './bookingMoment';

/**
 * Qué pasa cuando el asesor pide cerrar el modal (Escape, el botón de cerrar
 * o el backdrop).
 *
 * `confirm` no es una molestia: con la reserva retenida y sin confirmar, el
 * modal es el único lugar donde vive el reloj. Cerrarlo sin decir nada es
 * dejar una reserva viva del otro lado sin que nadie sepa hasta cuándo.
 */
export type BookingCloseIntent =
  /** Cerrar directo: no hay nada retenido esperando una decisión. */
  | { kind: 'close' }
  /**
   * Preguntar antes. `expiresAt` viene solo cuando el proveedor publicó
   * vencimiento: sin él la pregunta no inventa una hora.
   */
  | { kind: 'confirm'; expiresAt?: string };

export interface BookingCloseInput {
  moment: BookingMoment;
  /** Vencimiento publicado por el proveedor para ESTA reserva. */
  expiresAt?: string | null;
  now: number;
}

/**
 * La reserva sigue retenida ⇒ se pregunta; en cualquier otro momento se cierra
 * directo.
 *
 * El "sigue viva" sale de `holdCountdownAt`, el MISMO reloj que dibuja el chip
 * de la barra: así la pregunta no puede prometer una hora que el chip ya está
 * mostrando como vencida. Vencido se cierra sin preguntar —no hay nada que
 * cuidar— y sin vencimiento publicado (`unknown`) se pregunta igual, porque
 * ausencia no es negación: la reserva existe.
 */
export function resolveBookingClose({
  moment,
  expiresAt,
  now,
}: BookingCloseInput): BookingCloseIntent {
  // Confirmada, cancelada o todavía sin reservar: cerrar no abandona nada.
  if (moment !== 'reserved') return { kind: 'close' };

  const countdown = holdCountdownAt(expiresAt, now);
  if (countdown.status === 'expired') return { kind: 'close' };
  return countdown.status === 'unknown' || typeof expiresAt !== 'string'
    ? { kind: 'confirm' }
    : { kind: 'confirm', expiresAt };
}
